// @ts-nocheck
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import Auth from '@aws-amplify/auth';

import {
  getFilesByURL,
  updateSharedFilesWithAdditionalInfo,
  updateWithAdditionalInfo
} from '@features/files/utils';
import { ENVIRONMENT, PLATFORM } from '@aws';
import {
  FetchFilesResponse,
  File,
  ROOT_DIRECTORY,
  FilesOperationRequest,
  DownloadIdResponse,
  DownloadUrlResponse,
  DeleteUserDataResponse,
  ExportUsersParams,
  ExportUsersResponse
} from '@features/files/types';
import { EntityId } from '@reduxjs/toolkit';
import update from 'immutability-helper';
import { OWNED, RECEIVED, SHARED } from '@helpers';
import { UserProfileDataResponse } from './users/types';

const FILE = 'File' as const;
const FILE_DETAILS = 'File_Details' as const;
const FILE_COMMENTS = 'File_Comments' as const;
const OWNED_LIST = 'OWNED_LIST';
const SHARED_LIST = 'SHARED_LIST';
const RECEIVED_LIST = 'RECEIVED_LIST';

const getOwnedFilesUrl = (directoryId: string) =>
  `/files${directoryId === ROOT_DIRECTORY ? '/my' : `/${directoryId}`}`;

const getReceivedFilesUrl = (directoryId: string) =>
  `/files/received${directoryId === ROOT_DIRECTORY ? '' : `/${directoryId}`}`;
const getSharedFilesUrl = (directoryId: string) =>
  `/files/shared${directoryId === ROOT_DIRECTORY ? '' : `/${directoryId}`}`;

export const getTransformedPayloadToExtensivePayload = (
  ids: string[],
  parameters?: FilesOperationRequest<'EXTENSIVE_PAYLOAD'>['payload']['parameters']
): FilesOperationRequest<'EXTENSIVE_PAYLOAD'>['payload'] => {
  let data = {
    files: ids.map((id) => ({
      file_id: id
    })),
    parameters: {}
  };
  if (parameters) {
    data = update(data, {
      $merge: {
        parameters
      }
    });
  }

  return data;
};

const providesList = <R extends { file_id: string }[], T extends string>(
  resultsWithIds: R | undefined,
  tagType: T,
  listId?: string
) => {
  return resultsWithIds?.length
    ? [
        { type: tagType, id: listId },
        ...resultsWithIds.map(({ file_id }) => ({ type: tagType, id: file_id }))
      ]
    : [{ type: tagType, id: listId }];
};

export const apiSlice = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: PLATFORM[ENVIRONMENT].file_base_url,
    prepareHeaders: async (headers) => {
      const session = await Auth.currentSession();

      if (session) {
        headers.set('Authorization', `Bearer ${session.getIdToken().getJwtToken()}`);
      }

      return headers;
    }
  }),
  tagTypes: [FILE, FILE_DETAILS, FILE_COMMENTS],
  endpoints: (builder) => ({
    getOwnedFiles: builder.query<File[], string>({
      queryFn: async (directoryId: string, _queryApi, _extraOptions, fetchWithBaseQuery) => {
        let ownedFiles: File[] = [];

        const ownedFilesResult = await fetchWithBaseQuery(getOwnedFilesUrl(directoryId));
        const ownedFilesResultData = ownedFilesResult.data as FetchFilesResponse;
        const ownedFilesUrl = ownedFilesResultData?.url;

        if (ownedFilesUrl) {
          ownedFiles = await getFilesByURL(ownedFilesUrl);
        } else {
          ownedFiles = ownedFilesResultData?.files || [];
        }

        return { data: ownedFiles.map(updateWithAdditionalInfo) };
      },
      providesTags: (files: File[] | undefined) => providesList(files, FILE, OWNED_LIST)
    }),
    getReceivedFiles: builder.query<File[], string>({
      queryFn: async (directoryId: string, _queryApi, _extraOptions, fetchWithBaseQuery) => {
        let receivedFiles: File[] = [];

        const receivedFilesResult = await fetchWithBaseQuery(getReceivedFilesUrl(directoryId));
        const receivedFilesResultData = receivedFilesResult.data as FetchFilesResponse;
        const receivedFilesUrl = receivedFilesResultData?.url;

        if (receivedFilesUrl) {
          receivedFiles = await getFilesByURL(receivedFilesUrl);
        } else {
          receivedFiles = receivedFilesResultData?.files || [];
        }

        return { data: receivedFiles.map(updateWithAdditionalInfo) };
      },
      providesTags: (files: File[] | undefined) => providesList(files, FILE, RECEIVED_LIST)
    }),
    getSharedFiles: builder.query<File[], string>({
      queryFn: async (directoryId: string, _queryApi, _extraOptions, fetchWithBaseQuery) => {
        let sharedFiles: File[] = [];

        const sharedFilesResult = await fetchWithBaseQuery(getSharedFilesUrl(directoryId));
        const sharedFilesResultData = sharedFilesResult.data as FetchFilesResponse;
        const sharedFilesUrl = sharedFilesResultData?.url;

        if (sharedFilesUrl) {
          sharedFiles = await getFilesByURL(sharedFilesUrl);
        } else {
          sharedFiles = sharedFilesResultData?.files || [];
        }

        return { data: sharedFiles.map(updateSharedFilesWithAdditionalInfo) };
      },
      providesTags: (files: File[] | undefined) => providesList(files, FILE, SHARED_LIST)
    }),
    getAllFiles: builder.query<File[], string>({
      queryFn: async (directoryId: string, _queryApi, _extraOptions, fetchWithBaseQuery) => {
        const [ownedFilesResult, receivedFilesResult, sharedFilesResult] = await Promise.all([
          fetchWithBaseQuery(getOwnedFilesUrl(directoryId)),
          fetchWithBaseQuery(getReceivedFilesUrl(directoryId)),
          fetchWithBaseQuery(getSharedFilesUrl(directoryId))
        ]);

        const ownedFilesResultData = ownedFilesResult.data as FetchFilesResponse;
        const receivedFilesResultData = receivedFilesResult.data as FetchFilesResponse;
        const sharedFilesResultData = sharedFilesResult.data as FetchFilesResponse;

        const ownedFilesUrl = ownedFilesResultData?.url;
        const receivedFilesUrl = receivedFilesResultData?.url;
        const sharedFilesUrl = sharedFilesResultData?.url;

        const [ownedFiles, receivedFiles, sharedFiles] = await Promise.all([
          ownedFilesUrl ? getFilesByURL(ownedFilesUrl) : ownedFilesResultData?.files || [],
          receivedFilesUrl ? getFilesByURL(receivedFilesUrl) : receivedFilesResultData?.files || [],
          sharedFilesUrl ? getFilesByURL(sharedFilesUrl) : sharedFilesResultData?.files || []
        ]);

        const seen = new Set();

        return {
          data: sharedFiles
            .map((file) => ({ ...file, accessStatus: SHARED, isShared: true, isReceived: false }))
            .concat(
              receivedFiles.map((file) => ({
                ...file,
                accessStatus: RECEIVED,
                isShared: false,
                isReceived: true
              }))
            )
            .concat(
              ownedFiles.map((file) => ({
                ...file,
                accessStatus: OWNED,
                isShared: false,
                isReceived: false
              }))
            )
            .map(updateWithAdditionalInfo)
            .filter((el) => {
              const duplicate = seen.has(el.file_id);
              seen.add(el.file_id);
              return !duplicate;
            })
          // TODO add error response
        };
      },
      providesTags: (files: File[] | undefined) => {
        let tags = [
          { type: FILE, id: OWNED_LIST },
          { type: FILE, id: RECEIVED_LIST }
        ];

        if (files?.length) {
          tags = tags.concat(files.map(({ file_id }) => ({ type: FILE, id: file_id })));
        }

        return tags;
      }
    }),
    postUploadStatus: builder.mutation<any, any>({
      query: (payload: any) => ({
        url: '/uploads/status',
        method: 'POST',
        body: payload
      })
    }),
    getFilesDownloadId: builder.mutation<string, string[]>({
      query: (ids: string[]) => {
        return {
          url: '/files/download',
          method: 'POST',
          body: ids
        };
      },
      transformResponse: (response: DownloadIdResponse) => response.download_id
    }),
    getFilesDownloadLink: builder.mutation<DownloadUrlResponse, string | undefined>({
      query: (downloadId: string) => ({
        url: `/files/download/${downloadId}`,
        method: 'GET'
      })
    }),
    moveFiles: builder.mutation<void, { ids: string[]; destinationId: string }>({
      query: ({ ids, destinationId }) => ({
        url: '/files/operations',
        method: 'POST',
        body: {
          type: 'MOVE',
          payload: getTransformedPayloadToExtensivePayload(ids, { dir_id: destinationId })
        } as FilesOperationRequest<'EXTENSIVE_PAYLOAD'>
      }),
      invalidatesTags: [{ type: FILE }]
    }),
    refetchFiles: builder.mutation<null, void>({
      queryFn: () => ({ data: null }),
      invalidatesTags: [{ type: FILE }]
    }),
    deleteUserWithShairedFiles: builder.mutation<DeleteUserDataResponse, EntityId[]>({
      query: (usersIds: EntityId[]) => ({
        url: '/mynetwork',
        method: 'DELETE',
        body: {
          payload: {
            users: usersIds
          }
        }
      })
    }),
    exportUsers: builder.mutation<string, ExportUsersParams>({
      query: ({ usersIds, userRole }: ExportUsersParams) => ({
        url: `users/export/${userRole}`,
        method: 'POST',
        body: {
          payload: {
            users: usersIds
          }
        }
      }),
      transformResponse: (response: ExportUsersResponse) => response.download_id
    }),
    getCustomerAccountInfo: builder.query<
      { profile: NonNullable<UserProfileDataResponse['user']> },
      void
    >({
      query: () => `/user/profile`
    })
  })
});

export const {
  usePostUploadStatusMutation,
  useGetAllFilesQuery,
  useGetReceivedFilesQuery,
  useGetOwnedFilesQuery,
  useGetSharedFilesQuery,
  useGetFilesDownloadIdMutation,
  useGetFilesDownloadLinkMutation,
  useMoveFilesMutation,
  useDeleteUserWithShairedFilesMutation,
  useExportUsersMutation,
  useGetCustomerAccountInfoQuery
} = apiSlice;
